// src/pages/SettingsPage/components/GlobalAdminWorkspaceTable.tsx
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { NoteBlockContainer } from "../../note";
import {
  useAddWorkspace,
  useAllWorkspaces,
  useDeleteWorkspace,
  useFetchAllWorkspaces,
} from "../../../hook/settings";
import { initialWorkspaceFormData } from "./settings.contants";
import { WorkspaceFormData } from "./types/settings.types";
import { AddWorkspaceModal } from "./AddWorkSpaceModal";
import { LoadBoundary } from "../../brand/LoadBoundary";
import { LoadStatus } from "../../../hook/loadStatus";

export const GlobalAdminWorkspaceTable: React.FC = () => {
  const [allWorkspaces, setAllWorkspaces] = useAllWorkspaces();
  const fetchAllWorkspaces = useFetchAllWorkspaces();
  const theme = useTheme();
  const deleteWorkspace = useDeleteWorkspace();
  const addWorkspace = useAddWorkspace();
  const [formData, setFormData] = useState<WorkspaceFormData>(
    initialWorkspaceFormData
  );
  const [modalOpen, setModalOpen] = useState(false);

  const [status, setStatus] = useState<LoadStatus>(allWorkspaces.length === 0 ? "loading" : "ready");
  const load = () => {
    setStatus("loading");
    fetchAllWorkspaces().then(
      () => setStatus("ready"),
      () => setStatus("error"),
    );
  };

  useEffect(() => {
    // Fetch only if the list is empty
    if (allWorkspaces.length === 0) {
      load();
    }
  }, [allWorkspaces.length, fetchAllWorkspaces]); // Added dependencies

  if (status !== "ready") {
    return (
      <NoteBlockContainer name="Workspaces">
        <LoadBoundary
          status={status}
          onRetry={load}
          label="Loading workspaces"
          errorMessage="Could not load the workspaces."
          testId="workspaces-loading"
        />
      </NoteBlockContainer>
    );
  }

  return (
    <>
      <AddWorkspaceModal
        formData={formData}
        setFormData={setFormData}
        modalOpen={modalOpen}
        setModelOpen={setModalOpen}
        onSubmit={({ formData: { ownerEmail, appName, ownerName } }) => {
          addWorkspace({
            ownerEmail,
            appName,
            ownerName,
          });
        }}
      />
      <NoteBlockContainer name="Workspaces">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allWorkspaces.map((workspace) => {
              return (
                <TableRow key={workspace.id}>
                  <TableCell>{workspace.name}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        deleteWorkspace({ appId: workspace.id });
                      }}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ justifyContent: "end", display: "flex" }}>
          <IconButton
            onClick={() => setModalOpen(true)}
            sx={{ color: theme.palette.primary.main }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </NoteBlockContainer>
    </>
  );
};
